import { AccountFormContext } from "@shared/features/common/AccountForm.context";
import React, {
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  ClientData,
  ConfigurationExchangeInfo,
  OpenConfirmModal,
  WorkspaceType,
} from "@shared/types";
import { sharedLib } from "@shared/services/shared.service";
import { groupBy } from "lodash";
import useSharedWorkspace from "@shared/features/workspaces/useSharedWorkspace";
import { getAccountFullName } from "@shared/features/portfolio/utils";
import { ConnectExchangeTabs } from "@shared/features/portfolio/types";
import { ExternalConnectAccountContext } from "@shared/features/configuration/ExternalConnectAccount.context";
import { CreatedAccountData } from "@shared/features/onboarding/types";
import { sortItemsByExchangeOrder } from "@shared/utils/misc";
import { useBranding } from "@shared/utils/hooks/useBranding";
import {
  EXCHANGE_TO_PRIMARY_EXCHANGE,
  PRIMARY_EXCHANGES,
} from "@shared/common/constants";

const AccountFormProvider = ({ children }: PropsWithChildren) => {
  const {
    isExternalConnectAccount,
    accountName: externalAccountName,
    token,
  } = useContext(ExternalConnectAccountContext) || {};
  const { data: rawExchangesData, isLoading: exchangesLoading } =
    sharedLib.sharedApi.configuration.useGetExchangesListQuery({
      nested_exchanges: true,
      authToken: token,
    });

  const exchangesData = useMemo(
    () =>
      rawExchangesData?.map((exchange) => {
        const primaryExchange = EXCHANGE_TO_PRIMARY_EXCHANGE[exchange.exchange];
        return primaryExchange === PRIMARY_EXCHANGES.OKX_EU
          ? { ...exchange, nested_exchange: primaryExchange }
          : exchange;
      }),
    [rawExchangesData]
  );

  const [createAccountDrawerOpen, setCreateAccountDrawerOpen] = useState(false);
  const [tab, setTab] = useState<ConnectExchangeTabs>();
  const [selectedSubExchanges, setSelectedSubExchanges] =
    useState<ConfigurationExchangeInfo[]>();
  const [accountName, setAccountName] = useState<string>();
  const [accountNameError, setAccountNameError] = useState(false);
  const [addClientSucceeded, setAddClientSucceeded] = useState(false);
  const [createdAccounts, setCreatedAccounts] = useState<
    CreatedAccountData[] | undefined
  >([]);

  const { workspace } = useSharedWorkspace({});
  const { brandingData } = useBranding();
  const isDemoWorkspace =
    !isExternalConnectAccount && workspace === WorkspaceType.DEMO;

  useEffect(() => {
    if (externalAccountName) {
      setAccountName(externalAccountName);
    }
  }, [externalAccountName]);

  const resetFormValues = () => {
    setSelectedSubExchanges(undefined);
    setAddClientSucceeded(false);
    if (!isExternalConnectAccount) {
      setAccountName(undefined);
    }
  };

  const openCreateAccountDrawer = () => {
    setCreateAccountDrawerOpen(true);
  };

  const closeCreateAccountDrawer = () => {
    setCreateAccountDrawerOpen(false);
  };

  const sortedExchanges = useMemo(() => {
    let exchanges = exchangesData?.slice();
    const supportedExchanges = isExternalConnectAccount
      ? brandingData?.supported_exchanges
      : undefined;
    if (supportedExchanges) {
      exchanges = exchanges?.filter((exchange) =>
        supportedExchanges.includes(exchange.exchange)
      );
    }
    return sortItemsByExchangeOrder(exchanges);
  }, [exchangesData, brandingData, isExternalConnectAccount]);

  const aggExchanges = useMemo(
    () => groupBy(sortedExchanges, "nested_exchange"),
    [sortedExchanges]
  );

  const exchangeToClientName = useMemo(() => {
    if (!accountName) return;
    const exchangeToClientName: Record<string, string> = {};
    selectedSubExchanges?.map((subExchange) => {
      exchangeToClientName[subExchange.exchange] = getAccountFullName(
        accountName,
        subExchange
      );
    });
    return exchangeToClientName;
  }, [accountName, selectedSubExchanges]);

  const getSelectedSubExchangesData = useCallback(
    (selectedSubExchanges?: string[]) => {
      if (!exchangesData) return;
      return exchangesData.filter((exchange) =>
        selectedSubExchanges?.includes(exchange.exchange)
      );
    },
    [exchangesData]
  );

  const getSubExchanges = (exchangeToClientName: Record<string, string>) => {
    const exchanges = Object.keys(exchangeToClientName);
    return getSelectedSubExchangesData(exchanges);
  };

  const shouldShowSharedWalletWarning = (primaryExchange?: string) => {
    if (isDemoWorkspace) return;
    const subExchanges = primaryExchange ? aggExchanges[primaryExchange] : [];
    return (
      subExchanges.length !== selectedSubExchanges?.length &&
      subExchanges.some((subExchange) => subExchange.is_shared_wallet)
    );
  };

  const getSharedWalletWarningMessage = (primaryExchange?: string) => {
    return [
      `Please note that ${primaryExchange} uses a shared wallet across its sub-wallets (e.g., Spot and Futures). If you connect only one sub-wallet now (e.g., Spot) and later decide to add another (e.g., Futures), the system will treat them as separate wallets, and the shared assets will appear duplicated.`,
      "Recommendation: To avoid this, we recommend connecting all the sub-wallets you plan to use. If you decide to add more sub-wallets later, it will be recommended to delete the existing connection and reconnect all sub-wallets together.",
    ];
  };

  const handleOnSubmit = (
    primaryExchange: string | undefined,
    onSubmit: () => void,
    openConfirmModal: OpenConfirmModal
  ) => {
    if (shouldShowSharedWalletWarning(primaryExchange)) {
      openConfirmModal(
        getSharedWalletWarningMessage(primaryExchange),
        () => onSubmit(),
        undefined,
        undefined,
        "Continue",
        "Cancel"
      );
    } else {
      onSubmit();
    }
  };

  const getAccountSubExchange = useCallback(
    (account?: ClientData) => {
      if (!account) return;
      return exchangesData?.find(
        (subExchange) => subExchange.exchange === account?.exchange
      );
    },
    [exchangesData]
  );

  const contextValues = useMemo(() => {
    return {
      createAccountDrawerOpen,
      openCreateAccountDrawer,
      closeCreateAccountDrawer,
      exchangesData,
      exchangesLoading,
      aggExchanges,
      tab,
      setTab,
      isDemoWorkspace,
      selectedSubExchanges,
      setSelectedSubExchanges,
      accountName,
      setAccountName,
      resetFormValues,
      exchangeToClientName,
      getSelectedSubExchangesData,
      getSubExchanges,
      handleOnSubmit,
      accountNameError,
      setAccountNameError,
      getAccountSubExchange,
      addClientSucceeded,
      setAddClientSucceeded,
      createdAccounts,
      setCreatedAccounts,
    };
  }, [
    createAccountDrawerOpen,
    exchangesData,
    exchangesLoading,
    aggExchanges,
    tab,
    isDemoWorkspace,
    selectedSubExchanges,
    accountName,
    exchangeToClientName,
    getSelectedSubExchangesData,
    accountNameError,
    getAccountSubExchange,
    addClientSucceeded,
    createdAccounts,
  ]);

  return (
    <AccountFormContext.Provider value={contextValues}>
      {children}
    </AccountFormContext.Provider>
  );
};

export default React.memo(AccountFormProvider);
